import React, { Component } from 'react';
import { TextField, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEqual } from 'lodash';

import Question from './Question';
import { arbitraryStringShape } from '../../../types';
import { createArray } from '../../../utilities/arrayHelper';
import FirstColumnContainer from './FirstColumnContainer';

const styledBy = (property) => (props) => {
  const labelStyle = {
    red: 'red',
    green: 'green',
    darkkhaki: 'darkkhaki',
  };
  if (!props.floatingLabelStyle) {
    return 'inherit';
  }
  return labelStyle[props.floatingLabelStyle[property]];
};

const StyledToolTip = styled((props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Tooltip classes={{ tooltipPlacementBottom: 'placement-bottom' }} {...props} />
))`
  & .placement-bottom {
    font-size: 0.8em;
    margin: 0 4px 0 4px;
  }
`;

const TextFieldToolTip = ({
  tooltipText,
  disabled,
  ...others
}) => (
  <StyledToolTip
    title={tooltipText}
    disableFocusListener={!tooltipText}
    disableHoverListener={!tooltipText}
    disableTouchListener={!tooltipText}
  >
    {/* https://material-ui.com/components/tooltips/#disabled-elements */}
    <span>
      <TextField
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
            // eslint-disable-next-line react/jsx-props-no-spreading
        {...others}
        variant="standard"
      />
    </span>
  </StyledToolTip>
);

const StyledTextField = styled(TextFieldToolTip)`
  & label {
    color: ${styledBy('color')};
  }
  & label.Mui-focused {
    color: ${styledBy('color')};
  }
  & label.Mui-disabled {
    color: ${styledBy('color')};
  }
  & input {
    text-align: center;
  }
`;

export default class ArbitraryString extends Component {
  constructor(props) {
    super(props);

    this.state = {
      answers: [],
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    const currentProps = this.props.answerPayload;
    return !isEqual(currentProps, nextProps.answerPayload)
      || !isEqual(this.state.answers, nextState.answers)
      || !!this.props.disableIf
      || !isEqual(this.props.incomplete, nextProps.incomplete)
      || !isEqual(this.props.question.highlight, nextProps.question.highlight)
      || !isEqual(this.props.disabled, nextProps.disabled);
  }

  getValue = (caseIndex) => {
    if (this.state.answers[caseIndex] !== undefined) {
      return this.state.answers[caseIndex];
    }

    if (this.props.answerPayload
      && this.props.answerPayload.answers[caseIndex] !== undefined
      && this.props.answerPayload.answers[caseIndex]
    ) {
      return this.props.answerPayload.answers[caseIndex].answer;
    }
    return '';
  };

  setValue = (caseIndex, value) => {
    const answers = [...this.state.answers];
    answers[caseIndex] = value;
    this.setState({
      answers,
    });
  };

  checkIfDisabled = (sampleIndex) => {
    const condition = this.props.disableIf;
    if (!condition) {
      return false;
    }

    const answers = this.props.getSubmissionAnswer(condition.question);
    if (!answers || !answers[sampleIndex]) {
      return false;
    }
    const { answer } = answers[sampleIndex]; // OneOf answer has this shape
    switch (condition.operation) {
      case 'equal':
        return answer === condition.value;
      case 'unequal':
        return answer !== condition.value;
      case 'in':
        return condition.value.indexOf(answer) !== -1;
      default:
        throw Error(condition, 'The operation is not supported');
    }
  };

  renderValue(question, sampleIndex, placeholder, decorator) {
    const currentValue = this.getValue(sampleIndex);
    const decoration = decorator && decorator(sampleIndex, currentValue);
    const scoreLabel = decoration ? decoration.label : '';
    const placeholderText = (typeof placeholder === 'function') ? placeholder(sampleIndex) : placeholder;
    const id = `additionalQuestion-${question.uuid}-${sampleIndex}`;
    return (
      <StyledTextField
        style={{ width: 'auto' }}
        key={`${question.uuid}-${sampleIndex}`}
        data-id={id}
        placeholder={placeholderText ? placeholderText.toString() : ''}
        value={currentValue}
        onChange={(e) => this.setValue(sampleIndex, e.target.value)}
        disabled={this.props.disabled || this.checkIfDisabled(sampleIndex) || this.props.readonly}
        tooltipText={(typeof placeholder === 'function') ? placeholderText : `[P${sampleIndex + 1}] ${question.text}`}
        onBlur={(e) => this.props.onSamplePayloadChanged(
          question.uuid,
          sampleIndex,
          { type: 'ArbitraryString', answer: e.target.value },
        )}
        label={scoreLabel || ''}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...decoration}
      />
    );
  }

  renderValues(question, placeholder, decorator, questionStyle) {
    const text = question.text || question.de || question.en;
    return (
      <div key={question.uuid} className="mb4 mt4">
        <div className={!this.props.printFlag ? 'flex mb2 nowrap' : 'mb2'}>
          <FirstColumnContainer className="flex flex-column justify-center" style={{ whiteSpace: 'pre-wrap' }}>
            {
              questionStyle
                ? (
                  <Question
                    question={text}
                    uuid={question.uuid}
                    highlight={question.highlight}
                  />
                )
                : <span id={question.highlight ? `highlight-${question.uuid}` : undefined} className="pr1 fw4" style={this.props.incomplete ? { color: 'red' } : null}>{text}</span>
            }
          </FirstColumnContainer>
          <ul className="flex ma0 list nowrap pl0 basis-100">
            {
              createArray(this.props.numCases).map((_, sampleIndex) => {
                const key = `${question.uuid}-${sampleIndex}`;
                return (
                  <li
                    key={key}
                    className="mr2 basis-100 w2 flex justify-center"
                  >
                    {this.renderValue(question, sampleIndex, placeholder, decorator)}
                  </li>
                );
              })
            }
          </ul>
        </div>
      </div>
    );
  }

  render() {
    const {
      question, placeholder, decorator, questionStyle,
    } = this.props;
    return (
      <div>
        {this.renderValues(question, placeholder, decorator, questionStyle)}
      </div>
    );
  }
}

ArbitraryString.defaultProps = {
  printFlag: undefined,
};

TextFieldToolTip.propTypes = {
  disabled: Boolean.isRequired,
  tooltipText: String.isRequired,
};
ArbitraryString.propTypes = arbitraryStringShape.isRequired;
